import type { OhlcWindow } from "@/domains/thick-v1/stores/pool-ohlc-store"
import type { ManualClPoolOhlcPoint } from "@/domains/validators"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"

/**
 * OHLC-driven price chart for the pool detail page. Renders a pure SVG
 * polyline of closing prices across the chosen lookback window, alongside a
 * latest-close headline and signed percentage delta.
 *
 * The chart is intentionally dependency-free: it consumes already-sorted
 * {@link ManualClPoolOhlcPoint} rows from {@link PoolOhlcStore} and avoids
 * pulling a chart library in for a single surface. If we need candlesticks
 * later, swap to echarts (already installed) without changing the store.
 */
export interface PoolOhlcChartViewProps {
  /** Points sorted oldest-first. Empty array renders the empty state. */
  points: Array<ManualClPoolOhlcPoint>
  /** Currently-selected lookback window; drives the tab selection only. */
  window: OhlcWindow
  /** Latest close price, pre-formatted or undefined. */
  latestClose: number | undefined
  /** Signed delta vs the window open, as a decimal percentage. */
  priceDeltaPct: number | undefined
  /** Loading state — renders a subtle skeleton row over the chart. */
  isLoading: boolean
  /** User wants to swap the lookback; store updates and refetches. */
  onWindowChange: (next: OhlcWindow) => void
  /** Optional symbol pair for the y-axis label. */
  quoteSymbol?: string
  baseSymbol?: string
  className?: string
}

const WINDOW_OPTIONS: Array<{ id: OhlcWindow; label: string }> = [
  { id: "24h", label: "24H" },
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
]

const CHART_WIDTH = 600
const CHART_HEIGHT = 220
const CHART_PADDING = 8

export function PoolOhlcChartView({
  points,
  window,
  latestClose,
  priceDeltaPct,
  isLoading,
  onWindowChange,
  quoteSymbol,
  baseSymbol,
  className,
}: PoolOhlcChartViewProps) {
  const pathD = toPath(points)
  const deltaLabel = formatSignedPct(priceDeltaPct)
  const deltaTone =
    priceDeltaPct === undefined || !Number.isFinite(priceDeltaPct)
      ? "neutral"
      : priceDeltaPct >= 0
        ? "up"
        : "down"

  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5",
        className,
      )}
      data-testid="pool-ohlc-chart"
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <BodyXs className="font-medium uppercase tracking-wide text-t300">
            Price
          </BodyXs>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-xl font-medium text-t100">
              {latestClose !== undefined && Number.isFinite(latestClose)
                ? formatPrice(latestClose)
                : "—"}
            </span>
            {quoteSymbol && baseSymbol && (
              <span className="text-xs text-t200">
                {quoteSymbol} / {baseSymbol}
              </span>
            )}
            <span
              className={cn(
                "text-xs font-medium",
                deltaTone === "up" && "text-primary-400",
                deltaTone === "down" && "text-error-text",
                deltaTone === "neutral" && "text-t300",
              )}
            >
              {deltaLabel}
            </span>
          </div>
        </div>
        <nav
          className="inline-flex overflow-hidden rounded-md border border-p300-light bg-p300"
          aria-label="Chart timeframe"
        >
          {WINDOW_OPTIONS.map((option) => {
            const selected = option.id === window
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onWindowChange(option.id)}
                aria-pressed={selected}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  selected
                    ? "bg-p200 text-t100"
                    : "text-t200 hover:bg-p300-light",
                )}
              >
                {option.label}
              </button>
            )
          })}
        </nav>
      </header>
      <div className="relative">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Pool closing price over time"
          className={cn(
            "h-[220px] w-full text-primary-400",
            isLoading && "opacity-60",
          )}
        >
          {pathD ? (
            <path
              d={pathD}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              className="fill-t300 text-xs"
            >
              No price data yet
            </text>
          )}
        </svg>
        {isLoading && points.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-t300">
            Loading…
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Convert OHLC points to an SVG path. We plot `close` (or `open` fallback)
 * and normalise into the viewBox coordinate space so the chart scales on
 * any container width without JS re-layout.
 */
function toPath(points: Array<ManualClPoolOhlcPoint>): string | undefined {
  if (!points.length) return undefined
  const values = points.map((p) => (p.close ?? p.open ?? 0))
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const usableW = CHART_WIDTH - CHART_PADDING * 2
  const usableH = CHART_HEIGHT - CHART_PADDING * 2
  const step = values.length > 1 ? usableW / (values.length - 1) : 0
  return values
    .map((v, i) => {
      const x = CHART_PADDING + i * step
      const y = CHART_PADDING + usableH - ((v - min) / range) * usableH
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(" ")
}

function formatPrice(value: number): string {
  if (value === 0) return "0"
  const abs = Math.abs(value)
  if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 4 })
  return value.toLocaleString(undefined, { maximumFractionDigits: 6 })
}

function formatSignedPct(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  const sign = value >= 0 ? "+" : ""
  return `${sign}${value.toFixed(2)}%`
}
